// Element IQ API client. Typed fetch wrappers + SSE streaming for /api/chat.

import type {
  Brain, Thread, Message, CompanyContext, Settings, ChatEvent, AssetType, ThreadAsset,
} from './types';

const API_URL = 'https://element-iq-api.michael-4cf.workers.dev';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`POST ${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

// ─── Brains ──────────────────────────────────────────────────────
export async function listBrains(): Promise<Brain[]> {
  const r = await getJson<{ brains: Brain[] }>('/api/brains');
  return r.brains;
}

export async function getBrain(id: string): Promise<Brain> {
  const r = await getJson<{ brain: Brain }>(`/api/brains/${id}`);
  return r.brain;
}

// ─── Threads ─────────────────────────────────────────────────────
export async function listThreads(): Promise<Thread[]> {
  const r = await getJson<{ threads: Thread[] }>('/api/threads');
  return r.threads;
}

export async function createThread(initialBrainIds?: string[], title?: string): Promise<Thread> {
  const r = await postJson<{ thread: Thread }>('/api/threads', { initial_brain_ids: initialBrainIds, title });
  return r.thread;
}

export async function getThread(id: string): Promise<Thread> {
  const r = await getJson<{ thread: Thread }>(`/api/threads/${id}`);
  return r.thread;
}

export async function getThreadMessages(id: string): Promise<Message[]> {
  const r = await getJson<{ messages: Message[] }>(`/api/threads/${id}/messages`);
  return r.messages;
}

export async function updateThreadBrainIds(id: string, brainIds: string[]): Promise<Thread> {
  const r = await postJson<{ thread: Thread }>(`/api/threads/${id}/brain_ids`, { brain_ids: brainIds });
  return r.thread;
}

export async function renameThread(id: string, title: string): Promise<Thread> {
  const r = await postJson<{ thread: Thread }>(`/api/threads/${id}/title`, { title });
  return r.thread;
}

export async function setThreadPinned(id: string, pinned: boolean): Promise<Thread> {
  const r = await postJson<{ thread: Thread }>(`/api/threads/${id}/pin`, { pinned });
  return r.thread;
}

export async function deleteThread(id: string): Promise<void> {
  await postJson<{ ok: boolean }>(`/api/threads/${id}/delete`, {});
}

export async function generateAsset(threadId: string, type: AssetType): Promise<ThreadAsset> {
  const r = await postJson<{ asset: ThreadAsset }>(`/api/threads/${threadId}/assets`, { type });
  return r.asset;
}

export async function listThreadAssets(threadId: string): Promise<ThreadAsset[]> {
  const r = await getJson<{ assets: ThreadAsset[] }>(`/api/threads/${threadId}/assets`);
  return r.assets;
}

export async function listAllAssets(query?: string): Promise<ThreadAsset[]> {
  const path = query ? `/api/assets?q=${encodeURIComponent(query)}` : '/api/assets';
  const r = await getJson<{ assets: ThreadAsset[] }>(path);
  return r.assets;
}

export async function getAsset(id: string): Promise<ThreadAsset> {
  const r = await getJson<{ asset: ThreadAsset }>(`/api/assets/${id}`);
  return r.asset;
}

export async function deleteAsset(id: string): Promise<void> {
  await postJson<{ ok: boolean }>(`/api/assets/${id}/delete`, {});
}

// Truncate the thread at and after a given message — powers the edit+retry flow.
// Returns the count of removed messages.
export async function truncateThreadAtMessage(threadId: string, messageId: string): Promise<{ removed: number }> {
  return postJson<{ removed: number }>(`/api/threads/${threadId}/messages/${messageId}/truncate`, {});
}

// ─── Settings ────────────────────────────────────────────────────
export async function getSettings(): Promise<Settings> {
  const r = await getJson<{ settings: Settings }>('/api/settings');
  return r.settings;
}

export async function updateSettings(patch: Partial<Settings>): Promise<Settings> {
  const r = await postJson<{ settings: Settings }>('/api/settings', patch);
  return r.settings;
}

// ─── Company context ─────────────────────────────────────────────
export async function getCompanyContext(): Promise<CompanyContext | null> {
  const r = await getJson<{ company_context: CompanyContext | null }>('/api/company');
  return r.company_context;
}

export async function updateCompanyContext(patch: Partial<CompanyContext>): Promise<CompanyContext | null> {
  const r = await postJson<{ company_context: CompanyContext | null }>('/api/company', patch);
  return r.company_context;
}

// ─── Chat (SSE streaming) ────────────────────────────────────────
/**
 * Streams a chat response from POST /api/chat. Returns an async iterable of typed events.
 *
 * Native EventSource doesn't support POST, so we use fetch + ReadableStream and parse
 * the SSE protocol by hand. Buffer pattern handles partial chunks at chunk boundaries.
 */
export async function* chatStream(
  threadId: string,
  prompt: string,
  lens?: 'objective' | 'lens',
  modelOverride?: string,
  signal?: AbortSignal,
): AsyncGenerator<ChatEvent> {
  const res = await fetch(`${API_URL}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ thread_id: threadId, prompt, lens, model_override: modelOverride }),
    signal,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ message: `HTTP ${res.status}` }));
    yield { type: 'error', data: { message: errBody.error || errBody.message || `HTTP ${res.status}` } };
    return;
  }
  if (!res.body) {
    yield { type: 'error', data: { message: 'no response body' } };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by blank lines. Split, keep last partial chunk in buffer.
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const evt of events) {
        const parsed = parseSseEvent(evt);
        if (parsed) yield parsed;
      }
    }
    // Flush any final event in buffer (rare but possible)
    const tail = parseSseEvent(buffer);
    if (tail) yield tail;
  } catch (err: any) {
    yield { type: 'error', data: { message: err?.message ?? String(err) } };
  }
}

function parseSseEvent(raw: string): ChatEvent | null {
  if (!raw.trim()) return null;
  const lines = raw.split('\n');
  let eventType: string | null = null;
  let dataStr: string | null = null;
  for (const line of lines) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr = (dataStr ?? '') + line.slice(5).trimStart();
  }
  if (!eventType || dataStr === null) return null;
  try {
    const data = JSON.parse(dataStr);
    return { type: eventType, data } as ChatEvent;
  } catch {
    return null;
  }
}
